import { useCallback, useMemo } from 'react';
import { MutationFunctionContext, useIsMutating, useMutation, useQueryClient } from '@tanstack/react-query';

import { Either } from '@playq/irt';

import { removeQueriesExceptExcluded } from '/helpers';

import { unwrapEither } from './unwrapEither';
import { UseMutationOptionsExtended } from './types';

export const useEitherMutation = <ArgsType, LeftType, RightType, ContextType>(
  mutationFn: (variables: ArgsType, context?: MutationFunctionContext) => Promise<Either<LeftType, RightType>>,
  options?: UseMutationOptionsExtended<RightType, LeftType | Error, ArgsType, ContextType>
) => {
  const {
    onSuccess,
    removeQueriesKeys,
    removeQueriesKeysArray = [],
    excludedRemoveQueriesKeys,
    mutationKey,
  } = options ?? {};

  const queryClient = useQueryClient();
  const isMutating = useIsMutating({
    mutationKey,
    predicate: () => mutationKey !== undefined,
  });

  const handleSuccess = useCallback(
    (data: RightType, variables: ArgsType, onMutateResult: ContextType, context: MutationFunctionContext) => {
      onSuccess?.(data, variables, onMutateResult, context);
      if (isMutating <= 1) {
        removeQueriesExceptExcluded({
          queryClient,
          removeQueriesKeysArray: removeQueriesKeys ? [removeQueriesKeys] : removeQueriesKeysArray,
          excludedQueryKey: excludedRemoveQueriesKeys,
        });
      }
    },
    [queryClient, excludedRemoveQueriesKeys, removeQueriesKeys, removeQueriesKeysArray, isMutating, onSuccess]
  );

  const extendedOptions = useMemo(() => {
    if (options === undefined) {
      return;
    }

    return {
      ...options,
      onSuccess: handleSuccess,
    };
  }, [options, handleSuccess]);

  const wrappedMutationFn = (variables: ArgsType, ctx: MutationFunctionContext) =>
    unwrapEither(mutationFn(variables, ctx));

  return useMutation({
    mutationFn: wrappedMutationFn,
    ...extendedOptions,
  });
};
